import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { getMyProfile, getRepresentativeToken, saveRepresentativeToken } from '../services/representativeService';
import { safeNext } from '../utils/safeNext';

/**
 * /accesso-ticket#<token> — link diretto al ticket dalle email agli operatori (Task 27).
 *
 * - Il token viaggia nel FRAMMENTO: il browser non lo invia al server, a Nginx ne ad
 *   Analytics. Si legge una volta e si toglie subito dall'URL (history.replaceState):
 *   resta /accesso-ticket, route valida (al refresh: «Link non valido o già usato»).
 * - Legacy /accesso-ticket/:token (link inviati prima, validi 72 ore): stesso trattamento.
 * - La pagina non invia referrer.
 * - Sessione gia attiva dello stesso tipo e dello STESSO attore → si va al ticket
 *   senza chiamare lo scambio; di un ALTRO attore → avviso «Sei collegato come …»
 *   e bottone per aprire il ticket con l'account collegato.
 * - Altrimenti POST /support-link/exchange: la risposta e una sessione normale, salvata
 *   come fa il login (admin: AuthContext con refresh; rappresentante: representative_token).
 * - Errore (scaduto, revocato, non valido, accesso perso) → messaggio breve e login
 *   corretto con ?next=<percorso del ticket>.
 */
const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api';
const ERROR_REDIRECT_MS = 2500;

type LinkActor = 'user' | 'representative';
interface LinkInfo { actorType: LinkActor | null; ticketId: string | null; actorId: string | null }

// Token dal frammento (#<token>) o, per i link legacy, dal percorso.
export const readLinkToken = (routeToken: string | undefined, hash: string): string | undefined => {
  const fromHash = String(hash || '').replace(/^#/, '').trim();
  if (fromHash) return fromHash;
  return routeToken || undefined;
};

const decodePayload = (token: string | null | undefined): Record<string, unknown> | null => {
  try {
    const part = String(token || '').split('.')[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, '+').replace(/_/g, '/').padEnd(Math.ceil(part.length / 4) * 4, '=');
    const p = JSON.parse(atob(b64));
    return p && typeof p === 'object' ? p : null;
  } catch {
    return null;
  }
};

// Lettura NON verificata del payload: serve solo a scegliere area e destinazione.
// La verifica vera la fa il backend nello scambio (e le pagine del ticket).
export const readLinkInfo = (token: string | undefined): LinkInfo => {
  const p = decodePayload(token);
  if (!p) return { actorType: null, ticketId: null, actorId: null };
  const actorType = p.actorType === 'user' || p.actorType === 'representative' ? p.actorType : null;
  const ticketId = typeof p.ticketId === 'string' && /^[a-f\d]{24}$/i.test(p.ticketId) ? p.ticketId : null;
  const actorId = typeof p.actorId === 'string' && p.actorId ? p.actorId : null;
  return { actorType, ticketId, actorId };
};

export const ticketPathFor = (actorType: LinkActor, ticketId: string | null): string =>
  actorType === 'representative'
    ? (ticketId ? `/area-associativa/supporto/${ticketId}` : '/area-associativa/supporto')
    : (ticketId ? `/admin/centro-ticket?ticket=${ticketId}` : '/admin/centro-ticket');

export const loginPathFor = (actorType: LinkActor | null, next: string | null): string => {
  const base = actorType === 'representative' ? '/accesso-associati' : '/login';
  return next ? `${base}?next=${encodeURIComponent(next)}` : base;
};

// Sessione rappresentante presente e non scaduta (stesso controllo di RepresentativePrivateRoute):
// restituisce l'id del rappresentante collegato ('' se il token non lo riporta), o null.
const representativeSessionId = (): string | null => {
  const p = decodePayload(getRepresentativeToken());
  if (!p) return null;
  if (typeof p.exp === 'number' && p.exp <= Date.now() / 1000) return null;
  return p.representativeId ? String(p.representativeId) : '';
};

const adminName = (u: unknown): string => {
  const x = (u || {}) as { profile?: { firstName?: string; lastName?: string }; username?: string; email?: string };
  const full = `${x.profile?.firstName || ''} ${x.profile?.lastName || ''}`.trim();
  return full || x.username || x.email || 'un altro utente';
};

const SupportLinkAccessPage: React.FC = () => {
  const { token: routeToken } = useParams<{ token: string }>();
  const navigate = useNavigate();
  const { user, loading, loginWithTokens } = useAuth();
  // Il token si legge una volta sola e poi sparisce dall'URL (cronologia, referrer, screenshot).
  const tokenRef = useRef<string | undefined>(undefined);
  const infoRef = useRef<LinkInfo | null>(null);
  if (infoRef.current === null) {
    tokenRef.current = readLinkToken(routeToken, window.location.hash);
    infoRef.current = readLinkInfo(tokenRef.current);
  }
  const startedRef = useRef(false);
  const timerRef = useRef<number | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [loginPath, setLoginPath] = useState<string | null>(null);
  const [noToken, setNoToken] = useState(false);
  const [otherSession, setOtherSession] = useState<{ nome: string; target: string } | null>(null);

  useEffect(() => {
    try { window.history.replaceState(window.history.state, '', '/accesso-ticket'); } catch { /* ignora */ }
    return () => window.clearTimeout(timerRef.current);
  }, []);

  useEffect(() => {
    if (startedRef.current) return;
    const { actorType, ticketId, actorId } = infoRef.current as LinkInfo;
    const target = actorType ? ticketPathFor(actorType, ticketId) : null;

    const fail = (msg: string) => {
      startedRef.current = true;
      const to = loginPathFor(actorType, target);
      setError(msg);
      setLoginPath(to);
      timerRef.current = window.setTimeout(() => navigate(to, { replace: true }), ERROR_REDIRECT_MS);
    };

    // Refresh della pagina (token gia tolto) o link senza token: niente redirect automatico.
    if (!tokenRef.current) { startedRef.current = true; setNoToken(true); return; }
    if (!actorType) { fail('Link non valido.'); return; }

    // Admin: aspetta che AuthContext abbia verificato l'eventuale sessione salvata.
    if (actorType === 'user' && loading) return;
    startedRef.current = true;

    if (actorType === 'user' && user) {
      const u = user as { id?: string; _id?: string };
      const current = u.id || u._id;
      if (actorId && current && String(current) !== actorId) {
        setOtherSession({ nome: adminName(user), target: target as string });
        return;
      }
      navigate(target as string, { replace: true });
      return;
    }
    const repId = actorType === 'representative' ? representativeSessionId() : null;
    if (repId !== null) {
      if (actorId && repId && repId !== actorId) {
        setOtherSession({ nome: 'un altro profilo', target: target as string });
        getMyProfile()
          .then((res: { data?: { nome?: string; cognome?: string } }) => {
            const nome = `${res?.data?.nome || ''} ${res?.data?.cognome || ''}`.trim();
            if (nome) setOtherSession({ nome, target: target as string });
          })
          .catch(() => { /* resta il nome generico */ });
        return;
      }
      navigate(target as string, { replace: true });
      return;
    }

    (async () => {
      try {
        const res = await axios.post(`${API_BASE}/support-link/exchange`, { token: tokenRef.current });
        const d = res.data && res.data.data;
        const redirect = safeNext(d && d.redirect) || (target as string);
        if (d && d.actorType === 'user' && d.accessToken && d.refreshToken) {
          await loginWithTokens(d.accessToken, d.refreshToken);
        } else if (d && d.actorType === 'representative' && d.token) {
          saveRepresentativeToken(d.token);
        } else {
          throw new Error('Risposta non valida');
        }
        tokenRef.current = undefined;
        navigate(redirect, { replace: true });
      } catch (e) {
        const data = (e as { response?: { data?: { error?: string } } })?.response?.data;
        fail((data && data.error) || 'Non è stato possibile aprire il ticket.');
      }
    })();
  }, [loading, user, navigate, loginWithTokens]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 p-4">
      <Helmet>
        <meta name="referrer" content="no-referrer" />
        <meta name="robots" content="noindex" />
        <title>Apertura ticket - UNASCA</title>
      </Helmet>
      <div className="max-w-sm w-full text-center bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
        {noToken ? (
          <>
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">Link non valido o già usato</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Apri di nuovo il link dall&apos;email oppure accedi.</p>
            <div className="mt-4 flex flex-col items-center gap-2 text-sm">
              <Link to="/login" className="font-medium text-blue-600 dark:text-blue-400 hover:underline">Accesso operatori</Link>
              <Link to="/accesso-associati" className="font-medium text-blue-600 dark:text-blue-400 hover:underline">Accesso rappresentanti</Link>
            </div>
          </>
        ) : otherSession ? (
          <>
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">Sei collegato come {otherSession.nome}: il ticket si apre con il tuo account</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Il link era intestato a un altro profilo. Per usarlo esci e riapri il link dall&apos;email.</p>
            <button
              type="button"
              onClick={() => navigate(otherSession.target, { replace: true })}
              className="mt-4 inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700"
            >
              Apri il ticket
            </button>
          </>
        ) : error ? (
          <>
            <h1 className="text-base font-semibold text-gray-900 dark:text-white">{error}</h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">Accedi per aprire il ticket: dopo il login ti portiamo direttamente lì.</p>
            {loginPath && (
              <button
                type="button"
                onClick={() => navigate(loginPath, { replace: true })}
                className="mt-4 inline-flex items-center justify-center px-4 py-2 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700"
              >
                Vai all&apos;accesso
              </button>
            )}
          </>
        ) : (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-600 dark:text-gray-300" role="status">
            <Loader2 size={16} className="animate-spin" aria-hidden="true" /> Apertura del ticket…
          </div>
        )}
      </div>
    </div>
  );
};

export default SupportLinkAccessPage;
